import React from 'react';
import type { LeaderboardComponent } from '../../../types/builder';
import { Trophy, Medal, Award } from 'lucide-react';

interface LeaderboardRendererProps {
  component: LeaderboardComponent;
}

export const LeaderboardRenderer: React.FC<LeaderboardRendererProps> = ({ component }) => {
  const { title, entries, showAvatar, backgroundColor, textColor, padding, style } = component.settings;

  const leaderboardStyle = style || 'default';

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-bold">{rank}</span>;
  };

  // Default Style
  if (leaderboardStyle === 'default') {
    return (
      <div style={{ backgroundColor, color: textColor, padding: `${padding}px` }}>
        <div className="max-w-2xl mx-auto">
          {title && <h2 className="text-2xl font-bold text-center mb-6">{title}</h2>}
          <div className="space-y-3">
            {entries.map((entry) => (
              <div 
                key={entry.id} 
                className="flex items-center gap-4 bg-white/5 rounded-lg p-4 backdrop-blur-sm border border-white/10"
              >
                <div className="flex-shrink-0 w-12 flex items-center justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                {showAvatar && (
                  <div className="flex-shrink-0">
                    <img
                      src={entry.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.name)}&size=40&background=667eea&color=fff`}
                      alt={entry.name}
                      className="w-10 h-10 rounded-full"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-semibold">{entry.name}</div>
                </div>
                <div className="text-right font-bold text-green-500">{entry.prize}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Modern Style
  if (leaderboardStyle === 'modern') {
    return (
      <div style={{ backgroundColor, color: textColor, padding: `${padding}px` }}>
        <div className="max-w-2xl mx-auto">
          {title && <h2 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">{title}</h2>}
          <div className="space-y-2">
            {entries.map((entry, index) => (
              <div 
                key={entry.id} 
                className={`flex items-center gap-4 rounded-xl p-4 transition-all ${
                  index < 3 ? 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20' : 'bg-gray-100/50'
                }`}
              >
                <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full ${
                  entry.rank === 1 ? 'bg-yellow-500' : entry.rank === 2 ? 'bg-gray-300' : entry.rank === 3 ? 'bg-amber-600' : 'bg-gray-200'
                } text-white font-bold`}>
                  {entry.rank}
                </div>
                {showAvatar && (
                  <div className="flex-shrink-0">
                    <img
                      src={entry.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.name)}&size=48&background=667eea&color=fff`}
                      alt={entry.name}
                      className="w-12 h-12 rounded-full border-2 border-white shadow-lg"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-bold text-lg">{entry.name}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-xl text-green-600">{entry.prize}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Minimal Style
  if (leaderboardStyle === 'minimal') {
    return (
      <div style={{ backgroundColor, color: textColor, padding: `${padding}px` }}>
        <div className="max-w-xl mx-auto">
          {title && <h2 className="text-xl font-semibold text-center mb-4">{title}</h2>}
          <div className="space-y-2">
            {entries.map((entry) => (
              <div 
                key={entry.id} 
                className="flex items-center gap-3 py-2 border-b border-gray-200/50"
              >
                <div className="flex-shrink-0 w-8 text-center font-medium text-sm">{entry.rank}</div>
                {showAvatar && (
                  <img
                    src={entry.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.name)}&size=32&background=667eea&color=fff`}
                    alt={entry.name}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                <div className="flex-1 text-sm">{entry.name}</div>
                <div className="text-sm font-semibold text-green-600">{entry.prize}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

